import { findIndex, last, slice } from "lodash";
import { RelayEnvironmentProvider } from "react-relay";
import { Environment, Network, RecordSource, Store } from "relay-runtime";

import { sleep } from "@skydio/core";

import type { Decorator } from "@storybook/react";
import type { FetchFunction, GraphQLResponse } from "relay-runtime";

// Utility function to create mock data simulating a paginated API
export const makeMockPaginatedConnection = <
  T extends { id: string },
  V extends { cursor?: string | null; count: number },
>(
  data: T[],
  typename: string,
  { cursor, count }: V,
  filter: (item: T) => boolean = () => true
) => {
  const filteredItems = data.filter(item => filter(item));
  const firstIndex = cursor ? findIndex(filteredItems, item => item.id === cursor) + 1 : 0;
  const itemsAfterCursor = slice(filteredItems, firstIndex);
  const page = slice(itemsAfterCursor, 0, count);

  return {
    totalCount: filteredItems.length,
    edges: page.map(item => ({
      node: {
        __typename: typename,
        ...item,
      },
      cursor: item.id,
    })),
    pageInfo: {
      endCursor: last(page)?.id ?? null,
      hasNextPage: filteredItems.length > page.length,
    },
  };
};

export interface MockQueryArgs {
  queryName: string | string[];
  getData: (variables: any) => GraphQLResponse;
}

export const relayDecorator = (mockQueries: MockQueryArgs[], delay = 2000) => {
  const fetchQuery: FetchFunction = async (request, variables) => {
    for (const { queryName, getData } of mockQueries) {
      if (queryName === request.name || queryName.includes(request.name)) {
        const data = getData(variables);
        // About the length it takes to make a decent query in production
        await sleep(delay);
        return data;
      }
    }

    return {
      errors: [
        {
          message: `The query ${request.name} is not supported.`,
        },
      ],
    };
  };

  const mockEnvironment = new Environment({
    network: Network.create(fetchQuery),
    store: new Store(new RecordSource()),
  });

  const fn: Decorator = Story => (
    <RelayEnvironmentProvider environment={mockEnvironment}>
      <Story />
    </RelayEnvironmentProvider>
  );
  return fn;
};
